import { z } from 'zod';

/**
 * CPU 프로파일 계약 (8회차 결정 3).
 *
 * 에이전트가 `node:inspector`로 뜬 Chrome `.cpuprofile` JSON을 `POST /rt/profile`로 올린다.
 * 원본은 그대로 저장하고(DevTools Performance 탭에 그대로 넣을 수 있어야 한다),
 * 화면이 쓰는 요약은 저장 시 한 번만 계산해 옆에 둔다. 재계산은 없다.
 */

const nonneg = z.number().nonnegative();
const count = z.number().int().nonnegative();

/**
 * 파일명·URL에 그대로 쓰이므로 경로 문자가 들어오면 안 된다.
 * 점은 호스트명에 흔해서 허용하되 `..`는 막는다(상위 폴더로 새는 길).
 */
export const PROFILE_ID = /^(?!.*\.\.)[A-Za-z0-9._-]{1,200}$/;

/** 수집을 시작하게 만든 값. "왜 이때 떴는지"가 목록에서 바로 보여야 한다 */
export const ProfileTrigger = z.object({
  /** 트리거 시점에 관측한 이벤트루프 p99(ms) */
  loopP99Ms: nonneg,
  /** 그때 적용된 임계값(ms) */
  thresholdMs: nonneg,
  /** 임계값을 넘긴 연속 샘플 수 */
  consecutive: z.number().int().positive(),
});
export type ProfileTrigger = z.infer<typeof ProfileTrigger>;

/** 프로파일 한 건의 머리말. 목록 화면은 이것만 읽는다 */
export const ProfileMeta = z.object({
  /** `{instance}-{startedAt}`에서 파일명 안전 문자만 남긴 것 */
  id: z.string().regex(PROFILE_ID),
  service: z.string().min(1),
  instance: z.string().min(1),
  /** 수집 시작 시각. epoch ms */
  startedAt: z.number().int().nonnegative(),
  /** 실제 수집 길이(ms). 설정값이 아니라 잰 값 */
  durationMs: nonneg,
  trigger: ProfileTrigger,
  /** cpuprofile의 표본 수 */
  sampleCount: count,
  /** cpuprofile JSON 원본 바이트 수(압축 전) */
  sizeBytes: count,
});
export type ProfileMeta = z.infer<typeof ProfileMeta>;

/** 함수 단위로 합친 한 줄. 이름+파일+행이 같으면 한 줄이다 */
export const ProfileFrame = z.object({
  /** 함수명. 익명이면 `(anonymous)` */
  fn: z.string(),
  /** 스크립트 경로. V8이 안 주면 생략 */
  url: z.string().optional(),
  /** 1부터 세는 줄 번호 */
  line: z.number().int().positive().optional(),
  /** 그 함수 자신이 CPU를 쥐고 있던 시간 */
  selfMs: nonneg,
  /** 그 함수 아래 전부를 합친 시간. 재귀는 가장 바깥 한 번만 센다 */
  totalMs: nonneg,
  /** 전체 표본 시간 대비 self 비율(%) */
  selfPct: nonneg,
});
export type ProfileFrame = z.infer<typeof ProfileFrame>;

/** 상세 화면이 읽는 요약. 저장 시 한 번 계산해 `.summary.json`으로 둔다 */
export const ProfileSummary = z.object({
  meta: ProfileMeta,
  /** self 시간 내림차순 상위 N개 */
  top: z.array(ProfileFrame),
  /** 표본이 덮은 전체 시간(ms). self 비율의 분모 */
  totalMs: nonneg,
});
export type ProfileSummary = z.infer<typeof ProfileSummary>;

/**
 * 스냅샷에 싣는 프로파일러 상태 (결정 1).
 * `running`이면 지금 10초 수집 중, `cooldown`이면 방금 뜬 뒤 억제 구간이다.
 */
export const ProfileState = z.object({
  state: z.enum(['idle', 'running', 'cooldown']),
  /** 마지막 수집 시작 시각. 한 번도 안 떴으면 없다 */
  lastStartedAt: z.number().int().nonnegative().optional(),
  /** 프로세스가 뜬 뒤 수집한 누적 건수 */
  count,
});
export type ProfileState = z.infer<typeof ProfileState>;
